import { FormEventHandler, MutableRefObject, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Company,
  Destination,
  DevelopmentScale,
  Door,
  Engine,
  Factory,
  ProductionDivision,
  ProductionSearchConditions,
  TreeCompany,
} from '../../../../api/model';
import { CheckBox } from '../../../atoms/CheckBox';
import DatePicker from '../../../atoms/DatePicker';
import MultiSelect from '../../../atoms/MultiSelect';
import SingleSelect from '../../../atoms/SingleSelect';
import OrganizationTreeMultiSelect, { getOptions } from '../../../modules/OrganizationTreeMultiSelect';
import SlideToggle from '../../../modules/SlideToggle';
import MultiSelectStringArray from '../../schedulelist/MultiSelectStringArray';

interface Props {
  openSearchConditions: boolean;
  defaultConditions: ProductionSearchConditions;
  destinations: Destination[];
  modelNames: string[];
  doors: Door[];
  engines: Engine[];
  factories: Factory[];
  productionDevisions: ProductionDivision[];
  treeCompanies: TreeCompany[];
  companies: Company[];
  developmentScales: DevelopmentScale[];
  conditionsRef: MutableRefObject<ProductionSearchConditions>;
  setConditionsAndSearch: (value: ProductionSearchConditions) => void;
}

/**
 * 制作一覧検索条件表示
 * @param param0
 * @returns
 */
function ProductionsSearchConditions({
  openSearchConditions,
  defaultConditions,
  conditionsRef,
  destinations,
  modelNames,
  doors,
  engines,
  factories,
  productionDevisions,
  treeCompanies,
  companies,
  developmentScales,
  setConditionsAndSearch,
}: Props) {
  // フォームのデフォルト値
  const defaultValues = useMemo(() => {
    const modelUserIds = defaultConditions.model_user_ids?.map((x) => `user_${x}`);
    return { ...defaultConditions, model_user_ids: modelUserIds };
  }, [defaultConditions]);

  // フォーム情報
  const { control, register, handleSubmit } = useForm({ defaultValues });

  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // フォーム送信
  const onSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    void handleSubmit((data) => {
      // 組織コンポネントを使っているため、user_{number} の形でデータが送られるので number に変換する
      const modelUserIds =
        data.model_user_ids != null
          ? data.model_user_ids.flatMap((x) => {
              const splited = x.split('_');
              return splited.length === 2 ? parseInt(splited[1], 10) : [];
            })
          : undefined;
      const next: ProductionSearchConditions = {
        ...conditionsRef.current,
        production_no: data.production_no,
        destination_ids: data.destination_ids,
        sales_my: data.sales_my,
        model_names: data.model_names,
        title: data.title,
        production_division_ids: data.production_division_ids,
        door_ids: data.door_ids,
        engine_ids: data.engine_ids,
        factory_ids: data.factory_ids,
        model_user_ids: modelUserIds,
        maker_company_ids: data.maker_company_ids,
        development_scale_ids: data.development_scale_ids,
        complete_on_from: data.complete_on_from,
        complete_on_end: data.complete_on_end,
        include_completed: data.include_completed,
        deleted: data.deleted,
      };

      // 検索実行など
      setConditionsAndSearch(next);
    })();
  };

  return (
    <SlideToggle open={openSearchConditions}>
      <form id="production-search-conditions" onSubmit={onSubmit}>
        <div className="wfr scondition trigfr" style={{ display: 'block' }}>
          <div className="relativefr">
            <div className="sepfr3 mbs">
              <div className="labip1">
                <div>
                  <label htmlFor="production_no">{t('資料管理No')}</label>
                  <input type="text" id="production_no" {...register('production_no')} />
                </div>
                <div>
                  <label htmlFor="destination_ids">{t('仕向')}</label>
                  <MultiSelect
                    id="destination_ids"
                    name="destination_ids"
                    options={destinations}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.destination_id}
                    getLabel={(x) => x.destination_name}
                  />
                </div>
                <div>
                  <label htmlFor="sales_my">{t('M/Y')}</label>
                  <input type="text" id="sales_my" {...register('sales_my')} />
                </div>
                <div>
                  <label htmlFor="model_name">{t('機種名')}</label>
                  <MultiSelectStringArray
                    id="model_names"
                    name="model_names"
                    options={modelNames}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x}
                    getLabel={(x) => x}
                  />
                </div>
                <div>
                  <label htmlFor="title">{t('資料名')}</label>
                  <input type="text" id="title" {...register('title')} />
                </div>
                <div>
                  <label htmlFor="production_division_ids">{t('資料区分')}</label>
                  <MultiSelect
                    id="production_division_ids"
                    name="production_division_ids"
                    options={productionDevisions}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.production_division_id}
                    getLabel={(x) => x.production_division_name}
                  />
                </div>
              </div>
              <div className="labip1">
                <div>
                  <label htmlFor="door_ids">{t('ドア数')}</label>
                  <MultiSelect
                    id="door_ids"
                    name="door_ids"
                    options={doors}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.door_id}
                    getLabel={(x) => x.door_name}
                  />
                </div>
                <div>
                  <label htmlFor="engine_ids">{t('エンジン')}</label>
                  <MultiSelect
                    id="engine_ids"
                    name="engine_ids"
                    options={engines}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.engine_id}
                    getLabel={(x) => x.engine_name}
                  />
                </div>
                <div>
                  <label htmlFor="factory_ids">{t('工場')}</label>
                  <MultiSelect
                    id="factory_ids"
                    name="factory_ids"
                    options={factories}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.factory_id}
                    getLabel={(x) => x.factory_name}
                  />
                </div>
                <div>
                  <label htmlFor="model_user_ids">{t('機種担当')}</label>
                  <span className="dropdown">
                    <OrganizationTreeMultiSelect
                      id="model_user_ids"
                      name="model_user_ids"
                      control={control}
                      options={getOptions(treeCompanies, language === 'ja')}
                      selectableTypes={['user']}
                      isDisabled={false}
                    />
                  </span>
                </div>
              </div>
              <div className="labip1">
                <div>
                  <label htmlFor="maker_company_ids">{t('原作メーカー')}</label>
                  <MultiSelect
                    id="maker_company_ids"
                    name="maker_company_ids"
                    options={companies}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.company_id}
                    getLabel={(x) => x.company_name}
                  />
                </div>
                <div>
                  <label htmlFor="development_scale_ids">{t('開発規模')}</label>
                  <MultiSelect
                    id="development_scale_ids"
                    name="development_scale_ids"
                    options={developmentScales}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.development_scale_id}
                    getLabel={(x) => x.development_scale_name}
                  />
                </div>
                <div>
                  <label htmlFor="complete_on_from">{t('完成日From')}</label>
                  <DatePicker
                    id="complete_on_from"
                    name="complete_on_from"
                    isDisabled={false}
                    control={control}
                    displayMark
                  />
                </div>
                <div>
                  <label htmlFor="complete_on_end">{t('完成日End')}</label>
                  <DatePicker
                    id="complete_on_end"
                    name="complete_on_end"
                    isDisabled={false}
                    control={control}
                    displayMark
                  />
                </div>
              </div>
            </div>
            <div className="controlfr w50" />

            <div className="controlfr w50">
              {/*   
              <span className="ck">
                <CheckBox
                  name="f"
                  labelBefore={t('完成済み資料を含める').toString()}
                  register={register}
                />
              </span>
              */}
              <span className="ck" style={{ display: 'flex' }}>
                <label htmlFor="include_completed" style={{ paddingTop: '4px' }}>
                  {t('完成済')}
                </label>
                <SingleSelect
                  id="include_completed"
                  name="include_completed"
                  allowEmpty
                  options={[
                    { value: 1, label: t('完成済を含める') },
                    { value: 2, label: t('完成済のみ') },
                  ]}
                  register={register}
                  getValue={(x) => x.value}
                  getLabel={(x) => x.label}
                />
              </span>
              <span className="ck ml10">
                <CheckBox name="deleted" labelBefore={t('削除データ表示').toString()} register={register} />
              </span>
              <span>
                <input id="search" name="search" type="submit" value={t('検索').toString()} className="searchbtn" />
              </span>
            </div>
          </div>
        </div>
      </form>
    </SlideToggle>
  );
}

export default ProductionsSearchConditions;
